'use client'

import { useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { track } from '@/lib/analytics/track'

const CDN = 'https://cdn.soku.ai/marketing/videos/tools/minimax-h3-eval'

interface Finding {
  id: string
  eyebrow: string
  title: string
  body: string
  aspect: string
  left: { slug: string; label: string; caption: string }
  right: { slug: string; label: string; caption: string }
}

/**
 * Each finding is carried by a pair of clips run against the same source, so the
 * claim is checkable rather than asserted. Numbers quoted are from
 * scripts/marketing/eval-minimax-h3-editing.mjs.
 */
const FINDINGS: Finding[] = [
  {
    id: 'scene-not-image',
    eyebrow: 'Finding 01',
    title: 'H3 edits the scene, not the image',
    body:
      'Asking for a colour-science operation — "regrade to warm 35mm film: lifted blacks, halation, fine grain" — produced a clip that differed from the source by *less* than our do-nothing control did. Re-phrasing the identical intent as a fact about the world — "the scene is now lit by hazy warm late-afternoon sun" — put sunlight down the street. Post-production vocabulary is the thing that fails; describe what changed in the world instead.',
    aspect: 'aspect-video',
    left: { slug: 'ev-regrade', label: 'Post-production wording', caption: '"Regrade to warm 35mm film…" — SSIM 0.973 against a control floor of 0.973. Nothing happened.' },
    right: { slug: 'retry-regrade-scene', label: 'Scene-fact wording', caption: '"The scene is now lit by hazy warm late-afternoon sun." Warm/cool axis moves +2.3 against the control.' },
  },
  {
    id: 'keep-clause',
    eyebrow: 'Finding 02',
    title: 'A long "Keep" list can suppress the edit entirely',
    body:
      'MiniMax\'s own guidance is to name what must stay. It is real advice — drop the clause and H3 re-diffuses the frame. But we hit the opposite failure: a six-item preservation list paired with an over-specified change ("a structured cream linen blazer over a white tee") returned a byte-for-byte no-op. The same edit, written as one short sentence, landed cleanly and still held her face, hair, and backdrop.',
    aspect: 'aspect-[9/16]',
    left: { slug: 'ev-wardrobe', label: 'Six-item Keep list', caption: 'SSIM 0.951 — identical to the no-op control. The sweatshirt never changed.' },
    right: { slug: 'retry-wardrobe-simple', label: '"Keep everything else the same."', caption: 'SSIM 0.770. Jacket swapped, identity and backdrop intact.' },
  },
  {
    id: 'small-text',
    eyebrow: 'Finding 03',
    title: 'The documented "small text" weakness did not reproduce',
    body:
      'Every write-up of H3 repeats that small on-screen text is a weak spot, because MiniMax says so. At signage scale it held up fine for us: the lettering was rewritten in the same paint style, layout, and position, and separately removed to a blank board with the wood grain and shadow intact. Fine print and exact logo reproduction may still fail — we did not test those — but "H3 cannot do text" is too strong.',
    aspect: 'aspect-video',
    left: { slug: 'ev-edit-text', label: 'Rewrite the text', caption: '"MORNING ROAST" → "EVENING POUR", same font and board. SSIM 0.961 vs 0.963 control.' },
    right: { slug: 'ev-remove-sign', label: 'Remove the text', caption: 'Board wiped to blank, grain and bracket shadow preserved. SSIM 0.959.' },
  },
]

function ClipPair({ finding }: { finding: Finding }) {
  const [playing, setPlaying] = useState(false)
  const refs = useRef<(HTMLVideoElement | null)[]>([])

  const toggle = () => {
    const [a, b] = refs.current
    if (!a || !b) return
    if (playing) {
      a.pause()
      b.pause()
      setPlaying(false)
      return
    }
    a.currentTime = 0
    b.currentTime = 0
    void Promise.all([a.play(), b.play()]).catch(() => {
      if (a.paused && b.paused) setPlaying(false)
    })
    setPlaying(true)
    track('minimax_h3_eval_play', { finding: finding.id })
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {[finding.left, finding.right].map((side, i) => (
        <figure key={side.slug} className="flex flex-col gap-2">
          <Badge
            variant={i === 1 ? 'default' : 'outline'}
            className="w-fit rounded-full text-[10px]"
          >
            {side.label}
          </Badge>
          <div
            className={`group relative overflow-hidden rounded-xl border border-border bg-black ${finding.aspect}`}
          >
            <video
              ref={el => {
                refs.current[i] = el
              }}
              src={`${CDN}/${side.slug}.mp4`}
              poster={`${CDN}/${side.slug}.jpg`}
              muted
              loop
              playsInline
              preload="none"
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={toggle}
              className="absolute inset-0 flex items-center justify-center bg-black/10 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              aria-label={playing ? 'Pause comparison' : 'Play comparison'}
            >
              {playing ? (
                <Pause className="size-9 text-white/85" />
              ) : (
                <Play className="size-9 text-white/85" />
              )}
            </button>
          </div>
          <figcaption className="text-xs leading-5 text-muted-foreground">
            {side.caption}
          </figcaption>
        </figure>
      ))}
    </div>
  )
}

export function MinimaxH3EvalFindings() {
  return (
    <div className="flex flex-col gap-14">
      {FINDINGS.map(finding => (
        <article key={finding.id} className="flex flex-col gap-5">
          <div>
            <span className="text-xs font-medium tracking-[0.3em] text-muted-foreground uppercase">
              {finding.eyebrow}
            </span>
            <h3 className="mt-2 text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              {finding.title}
            </h3>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base">
              {finding.body}
            </p>
          </div>
          <ClipPair finding={finding} />
        </article>
      ))}
    </div>
  )
}
